import {
  Checkbox,
  createTheme,
  Input,
  InputWrapper,
  Radio,
  Switch,
  TextInput,
  type CheckboxProps,
  type InputProps,
  type MantineTheme,
  type RadioProps,
  type SwitchProps,
} from "@mantine/core";
import { shade } from "./colors";

const computePaddingInline = (
  theme: MantineTheme,
  props: SwitchProps | RadioProps | CheckboxProps
) => {
  switch (props.labelPosition) {
    case "left":
      return { "padding-inline-end": theme.spacing.xs };
    case "right":
      return { "padding-inline-start": theme.spacing.xs };
  }
};

const SIZE_KEYS = ["xs", "sm", "md", "lg", "xl"] as const;

const buildSizeScale = (cssVarPrefix: string, base: number, step: number) =>
  Object.fromEntries(
    SIZE_KEYS.map((key, index) => [
      `--${cssVarPrefix}-${key}`,
      `${base + index * step}px`,
    ])
  );

const computeFocusBoxShadow = (props: InputProps) => {
  if (props.error) {
    return { boxShadow: "0 0 0 3px var(--mantine-color-error-1)" };
  }

  if (props.success) {
    return { boxShadow: "0 0 0 3px var(--mantine-color-success-1)" };
  }

  return { boxShadow: "0 0 0 3px var(--mantine-color-brand-1)" };
};

const computeInputColorOverrides = (props: InputProps) => {
  if (props.error) {
    return {
      "--input-placeholder-color": "var(--mantine-color-placeholder)",
    };
  }

  if (props.success) {
    return {};
  }

  return {
    "--input-bd": "var(--mantine-color-default-border)",
  };
};

export const FormsTheme = createTheme({
  components: {
    // IN SHAPE
    Input: Input.extend({
      styles: (theme, props) => {
        if (props.variant === "unstyled") {
          return {};
        }

        return {
          input: {
            "--input-color": shade(theme.colors.ink, 900),
            ...computeInputColorOverrides(props),
            "&:focus, &:focus-within": computeFocusBoxShadow(props),
          },
        };
      },
    }),

    InputWrapper: InputWrapper.extend({
      styles: theme => {
        const errorColor = shade(theme.colors.error, 700);

        return {
          error: {
            display: "flex",
            alignItems: "center",
            gap: "4px",
            color: errorColor,
            "&::before": {
              content: '""',
              flexShrink: 0,
              width: "24px",
              height: "24px",
              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(errorColor)}' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><circle cx='12' cy='12' r='10'/><line x1='12' x2='12' y1='8' y2='12'/><line x1='12' x2='12.01' y1='16' y2='16'/></svg>")`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
            },
          },
        };
      },
    }),

    TextInput: TextInput.extend({
      defaultProps: {
        placeholder: "",
      },
      styles: (theme, props) => {
        if (props.variant === "unstyled") {
          return {};
        }

        return {
          input: {
            //  If filled & not focus => border darker
            "&:not(:placeholder-shown):not(:focus):not([data-error]):not([data-success])":
              {
                "--input-bd": shade(theme.colors.ink, 300),
              },
            "&:read-only:read-only:not(:disabled)": {
              "--input-bd": shade(theme.colors.ink, 200),
              borderStyle: "dashed",
              backgroundColor: shade(theme.colors.ink, 50),
              color: shade(theme.colors.ink, 700),
              cursor: "text",
            },
          },
        };
      },
    }),
    Switch: Switch.extend({
      defaultProps: {
        size: "sm",
        labelPosition: "left",
        withThumbIndicator: false,
      },
      styles: (theme, props) => {
        return {
          label: computePaddingInline(theme, props),
          description: computePaddingInline(theme, props),
          error: computePaddingInline(theme, props),
        };
      },
    }),
    Radio: Radio.extend({
      defaultProps: {
        size: "xs",
        labelPosition: "right",
      },
      styles: (theme, props) => {
        return {
          root: {
            ...buildSizeScale("radio-size", 18, 4),
            ...buildSizeScale("radio-icon-size", 7, 2),
          },
          label: computePaddingInline(theme, props),
          description: computePaddingInline(theme, props),
          error: computePaddingInline(theme, props),
        };
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        radius: "xs",
        size: "xs",
      },
      styles: (theme, props) => {
        return {
          root: buildSizeScale("checkbox-size", 18, 4),
          icon: {
            width: "50%",
          },
          label: computePaddingInline(theme, props),
          description: computePaddingInline(theme, props),
          error: computePaddingInline(theme, props),
        };
      },
    }),
  },
});
